import React, { Fragment, useContext, useEffect, useState } from "react";
import moment from "moment";
import { fetchOrderByUser } from "./Action";
import Layout, { DashboardUserContext } from "./Layout";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const apiURL = process.env.REACT_APP_API_URL;
const logoImage = "/assets/logo-bloom.jpg";

const formatRupiah = (amount) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
  }).format(amount);

const TableHeader = () => (
  <thead className="bg-gray-100 sticky top-0 z-10">
    <tr>
      <th className="w-[60px] px-4 py-2 border text-center text-sm">No</th>
      <th className="w-[300px] px-10 py-2 border text-center text-sm">Products</th>
      <th className="w-[300px] px-10 py-2 border text-center text-sm">Status</th>
      <th className="w-[100px] px-10 py-2 border text-center text-sm">Total</th>
      <th className="w-[150px] px-10 py-2 border text-center text-sm">Phone</th>
      <th className="w-[250px] px-10 py-2 border text-center text-sm">Address</th>
      <th className="w-[300px] px-15 py-2 border text-center text-sm">Transaction Id</th>
      <th className="w-[200px] px-4 py-2 border text-center text-sm">Payment Method</th>
      <th className="w-[180px] px-5 py-2 border text-center text-sm">Created at</th>
      <th className="w-[180px] px-4 py-2 border text-center text-sm">Updated at</th>
    </tr>
  </thead>
);

const TableBody = ({ order, index }) => (
  <tr className="text-center text-sm">
    <td className="p-2 border">{index + 1}</td>
    <td className="p-2 border">
      <div className="flex flex-col space-y-1">
        {order.allProduct.map((product, i) => (
          <div key={i} className="flex items-center gap-2">
            <img
              className="w-8 h-8 object-cover"
              src={`${apiURL}/uploads/products/${product.id?.pImages?.[0]}`}
              alt={product.id?.pName}
            />
            <span>{product.id?.pName || "Unknown"}</span>
            <span>{product.quantity}x</span>
          </div>
        ))}
      </div>
    </td>
    <td className="p-2 border">
      <span className={`text-xs font-semibold ${
        order.status === "Not processed" ? "text-red-600" :
        order.status === "Processing" ? "text-yellow-600" :
        order.status === "Shipped" ? "text-blue-600" :
        order.status === "Delivered" ? "text-green-600" :
        order.status === "Ready to pick up at the store" ? "text-green-600" :
        "text-gray-600"
      }`}>
        {order.status}
      </span>
    </td>
    <td className="p-2 border">{formatRupiah(order.amount)}</td>
    <td className="p-2 border">{order.phone}</td>
    <td className="p-2 border">{order.address}</td>
    <td className="p-2 border">{order.transactionId}</td>
    <td className="p-2 border">{order.paymentMethod || "Unknown"}</td>
    <td className="p-2 border">{moment(order.createdAt).format("lll")}</td>
    <td className="p-2 border">{moment(order.updatedAt).format("lll")}</td>
  </tr>
);

const OrdersComponent = () => {
  const { data, dispatch } = useContext(DashboardUserContext);
  const { OrderByUser: orders } = data;
  const [filter, setFilter] = useState("");
  const [page, setPage] = useState(1);
  const ordersPerPage = 5;

  useEffect(() => {
    fetchOrderByUser(dispatch);
  }, [dispatch]);

  const filteredOrders = orders?.filter((o) =>
    filter ? o.status.toLowerCase() === filter.toLowerCase() : true
  );

  const paginatedOrders = filteredOrders?.slice(
    (page - 1) * ordersPerPage, page * ordersPerPage
  );
  const totalPages = Math.ceil((filteredOrders?.length || 0) / ordersPerPage);

  const handleExportPDF = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "A4" });
    const pageWidth = doc.internal.pageSize.getWidth();

    const img = new Image();
    img.src = logoImage;
    doc.addImage(img, "JPEG", pageWidth / 2 - 40, 20, 60, 60);

    doc.setFontSize(16);
    doc.text("FRESH FLOWERS INDONESIA — Order Report", pageWidth / 2, 110, { align: "center" });

    if (orders[0]?.user?.name) {
      doc.setFontSize(12);
      doc.text(`Customer: ${orders[0].user.name}`, pageWidth / 2, 130, { align: "center" });
    }

    const tableColumn = [
      "No", "Products", "Status", "Total", "Phone", 
      "Address", "Transaction ID", "Payment", "Created At"
    ];

    const tableRows = [];
    filteredOrders.forEach((order, idx) => {
      const productNames = order.allProduct
        .map((p) => `${p.id?.pName || ""} x${p.quantity}`)
        .join(", ");
      const row = [
        idx + 1,
        productNames,
        order.status,
        formatRupiah(order.amount),
        order.phone,
        order.address,
        order.transactionId,
        order.paymentMethod || "Unknown",
        moment(order.createdAt).format("lll")
      ];
      tableRows.push(row);
    });

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 150,
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [48, 48, 49], textColor: 255, halign: "center" },
      bodyStyles: { halign: "center" },
      margin: { left: 40, right: 40 },
      theme: "striped"
    });

    doc.save("nda_orders.pdf");
  };

  return (
    <Fragment>
      <div className="flex flex-col w-full my-4 px-4">
        <div className="border-t-4 border-yellow-700 rounded shadow-md bg-white">
          <div className="flex justify-between items-center px-4 py-3 border-b bg-gray-50">
            <span className="text-lg font-semibold">Orders</span>
            <div className="flex gap-3">
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="border px-3 py-1 rounded text-sm focus:outline-none"
              >
                <option value="">All</option>
                <option value="Not processed">Not processed</option>
                <option value="Processing">Processing</option>
                <option value="Shipped">Shipped</option>
                <option value="Delivered">Delivered</option>
                <option value="Ready to pick up at the store">Ready to pick up at the store</option>
                <option value="Cancelled">Cancelled</option>
              </select>
              <button
                onClick={handleExportPDF}
                className="bg-yellow-700 hover:bg-blue-700 text-white text-sm px-3 py-1 rounded"
              >
                Export PDF
              </button>
            </div>
          </div>

          <div className="overflow-x-auto px-4">
            <table className="min-w-[2000px] table-auto border border-collapse my-2">
              <TableHeader />
              <tbody>
                {paginatedOrders?.length > 0 ? (
                  paginatedOrders.map((order, i) => (
                    <TableBody key={i} order={order} index={(page - 1) * ordersPerPage + i} />
                  ))
                ) : (
                  <tr>
                    <td colSpan="10" className="text-xl text-center font-semibold py-8">
                      No order found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between items-center px-4 py-2">
            <span className="text-sm text-gray-600">
              Total {filteredOrders?.length || 0} order found
            </span>
            <div className="flex gap-2">
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  onClick={() => setPage(i + 1)}
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    page === i + 1 ? "bg-yellow-700 text-white" : "bg-gray-200 text-gray-700"
                  }`}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

const UserOrders = () => <Layout children={<OrdersComponent />} />;

export default UserOrders;
